import { useCallback, useEffect, useState } from 'react'
import { VideoCard, VideoConferenceCard } from '@/entities/video'
import type { VideoCardProps, VideoConferenceCardProps } from '@/entities/video'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn, useDraggableHorizontalScroll } from '@/shared/lib'

type VideoShelfVideoItem = {
  id: string
  kind?: 'video'
} & VideoCardProps

type VideoShelfConferenceItem = {
  id: string
  kind: 'conference'
} & VideoConferenceCardProps

export type VideoShelfItem = VideoShelfVideoItem | VideoShelfConferenceItem

export type VideoShelfProps = {
  title: React.ReactNode
  items: VideoShelfItem[]
  showViewAll?: boolean
  forceShowViewAll?: boolean
  viewAllLabel?: string
  onViewAllClick?: () => void
  onEndReached?: () => void
  isViewAllDisabled?: boolean
  bleedRight?: boolean
  className?: string
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.onboarding.titleDesktop),
}

const viewAllStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: 'rgba(254, 254, 254, 0.7)',
  ...typographyStyle(designTokens.typography.body.base),
}

export function VideoShelf({
  title,
  items,
  showViewAll = true,
  forceShowViewAll = false,
  viewAllLabel = 'Посмотреть все',
  onViewAllClick,
  onEndReached,
  isViewAllDisabled = false,
  bleedRight = false,
  className,
}: VideoShelfProps) {
  const [shelfScrollRef, isShelfDragging, bindDragAwareClick] =
    useDraggableHorizontalScroll<HTMLDivElement>()
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)

  useEffect(() => {
    if (forceShowViewAll) {
      return
    }

    const shelf = shelfScrollRef.current
    if (!shelf) {
      return
    }

    const updateOverflowState = () => {
      setHasHorizontalOverflow(shelf.scrollWidth - shelf.clientWidth > 8)
    }

    updateOverflowState()

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateOverflowState()
      })
      resizeObserver.observe(shelf)

      for (const child of Array.from(shelf.children)) {
        resizeObserver.observe(child)
      }

      return () => {
        resizeObserver.disconnect()
      }
    }

    window.addEventListener('resize', updateOverflowState)
    return () => {
      window.removeEventListener('resize', updateOverflowState)
    }
  }, [forceShowViewAll, items.length, shelfScrollRef])

  const shouldRenderViewAll = showViewAll && (forceShowViewAll || hasHorizontalOverflow)

  const maybeLoadMoreOnEnd = useCallback(() => {
    const shelf = shelfScrollRef.current
    if (!shelf || !onEndReached || isViewAllDisabled || !shouldRenderViewAll) {
      return
    }

    const remainingScroll = shelf.scrollWidth - shelf.scrollLeft - shelf.clientWidth
    if (remainingScroll <= 160) {
      onEndReached()
    }
  }, [isViewAllDisabled, onEndReached, shelfScrollRef, shouldRenderViewAll])

  useEffect(() => {
    maybeLoadMoreOnEnd()
  }, [items.length, maybeLoadMoreOnEnd])

  return (
    <section className={cn('flex w-full flex-col gap-4', className)}>
      <div
        className={cn(
          'flex w-full items-center',
          shouldRenderViewAll ? 'justify-between' : 'justify-start'
        )}
      >
        <h2 style={titleStyle}>{title}</h2>
        {shouldRenderViewAll ? (
          <button
            type="button"
            style={viewAllStyle}
            onClick={onViewAllClick}
            disabled={isViewAllDisabled}
            className={cn('cursor-pointer', isViewAllDisabled ? 'opacity-60' : undefined)}
          >
            {viewAllLabel}
          </button>
        ) : null}
      </div>
      <div
        ref={shelfScrollRef}
        className={cn(
          'scrollbar-none flex w-full gap-4 overflow-x-auto pb-1',
          bleedRight ? 'w-[calc(100%+1.5rem)] max-w-none' : undefined,
          isShelfDragging ? 'cursor-grabbing select-none' : 'cursor-default'
        )}
        onScroll={maybeLoadMoreOnEnd}
      >
        {items.map((item) => {
          if (item.kind === 'conference') {
            const { id, kind: _kind, ...card } = item
            return (
              <VideoConferenceCard key={id} {...card} onClick={bindDragAwareClick(card.onClick)} />
            )
          }

          const { id, kind: _kind, ...card } = item
          return <VideoCard key={id} {...card} onClick={bindDragAwareClick(card.onClick)} />
        })}
      </div>
    </section>
  )
}

VideoShelf.displayName = 'VideoShelf'
